/**
 * A bounded ring of recent console errors, for the #8 debug bundle.
 *
 * WHY. The defects found in the 2026-07-27 sweep — a stale precached bundle, a
 * dropped VITE_MARK_ENDPOINT, a wedged outbox — all announced themselves in the
 * console and nowhere else. On a phone there is no console to look at, so those
 * signals were simply lost and each bug had to be re-found by hand. This keeps
 * the last few so a super can send them.
 *
 * Deliberately small and deliberately dumb: the last N `console.error`/`warn`
 * lines, each truncated. It is a diagnostic aid, not a logging framework, and it
 * must never become a memory pressure or a privacy surface of its own — see the
 * length caps below. Installed once at app start; the original console methods
 * are always called through, so nothing changes for anyone debugging normally.
 */

const MAX_ENTRIES = 20;
const MAX_LEN = 300;

export interface ConsoleEntry {
  ts: string;
  level: "error" | "warn";
  text: string;
}

let ring: ConsoleEntry[] = [];
let installed = false;

function render(args: unknown[]): string {
  const text = args
    .map((a) => {
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      if (typeof a === "string") return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(" ");
  return text.length > MAX_LEN ? `${text.slice(0, MAX_LEN)}…` : text;
}

function push(level: "error" | "warn", args: unknown[]): void {
  ring.push({ ts: new Date().toISOString(), level, text: render(args) });
  if (ring.length > MAX_ENTRIES) ring = ring.slice(-MAX_ENTRIES);
}

/** Idempotent — double-installing would double-wrap console and duplicate every
 * line, which is exactly the kind of thing that only shows up in the field. */
export function installConsoleBuffer(): void {
  if (installed || typeof console === "undefined") return;
  installed = true;
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);
  console.error = (...args: unknown[]) => {
    push("error", args);
    origError(...args);
  };
  console.warn = (...args: unknown[]) => {
    push("warn", args);
    origWarn(...args);
  };
}

export function recentConsoleEntries(): ConsoleEntry[] {
  return [...ring];
}

/** Test-only reset, matching the codebase's `_reset*ForTests` convention. */
export function _resetConsoleBufferForTests(): void {
  ring = [];
}
